export interface Property {
  id: string;
  code: string;
  name: string;
  province: string;
  region: string;
  lat: number;
  lon: number;
  efficiency: number | null; // This now represents EUI value (kWh/m²·yr)
  isOnline: boolean;
  hasAlarm?: boolean;
  lastUpdated: string | null;
  image?: string;
  metrics: {
    powerConsumption: number | null;
    electricityConsumption: number | null;
    accumulatedRth: number | null;
  } | null;
}

// EUI thresholds matching retail & wholesale building standards
export const EFFICIENCY_THRESHOLD = [126, 161, 266, 298, 370];
// Color scale using your palette - balanced for visibility and emotion
export const EFFICIENCY_COLOR = [
  '#0E7EE4',  // Blue 50 - ZEB (excellent efficiency)
  '#14B0BC',  // Success 50 - ECON (good performance) 
  '#EA8F28',  // Orange 40 - HEPS (moderate efficiency)
  '#FE4C00',  // Bright Orange - BEC (below standard)
  '#800000'   // Danger 50 - REF (poor efficiency)
];

export const mockProperties: Property[] = [
  // Bangkok Region
  {
    id: '1',
    code: 'BANG',
    name: 'Bangna',
    province: 'Bangkok',
    region: 'Central',
    lat: 13.6692,
    lon: 100.6067,
    efficiency: 145,
    isOnline: true,
    hasAlarm: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 4200,
      electricityConsumption: 3850,
      accumulatedRth: 5200
    }
  },
  {
    id: '2',
    code: 'RAMA',
    name: 'Rama 2',
    province: 'Bangkok',
    region: 'Central',
    lat: 13.6628,
    lon: 100.4372,
    efficiency: 120,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3800,
      electricityConsumption: 3400,
      accumulatedRth: 4800
    }
  },
  {
    id: '3',
    code: 'RANG',
    name: 'Rangsit',
    province: 'Pathum Thani',
    region: 'Central',
    lat: 13.9896,
    lon: 100.6178,
    efficiency: 280,
    isOnline: true,
    hasAlarm: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3600,
      electricityConsumption: 3200,
      accumulatedRth: 4500
    }
  },
  {
    id: '4',
    code: 'CHAENG',
    name: 'Chaengwattana',
    province: 'Nonthaburi',
    region: 'Central',
    lat: 13.9026,
    lon: 100.5234,
    efficiency: 195,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3900,
      electricityConsumption: 3500,
      accumulatedRth: 4900
    }
  },
  {
    id: '5',
    code: 'SRIN',
    name: 'Srinakarin',
    province: 'Bangkok',
    region: 'Central',
    lat: 13.6934,
    lon: 100.6469,
    efficiency: 350,
    isOnline: false,
    lastUpdated: '22-05-2025',
    metrics: {
      powerConsumption: 0,
      electricityConsumption: 0,
      accumulatedRth: 0
    }
  },
  // Central Region
  {
    id: '6',
    code: 'AYUT',
    name: 'Ayutthaya',
    province: 'Ayutthaya',
    region: 'Central',
    lat: 14.3692,
    lon: 100.5877,
    efficiency: 142,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3200,
      electricityConsumption: 2900,
      accumulatedRth: 4100
    }
  },
  {
    id: '7',
    code: 'SARA',
    name: 'Saraburi',
    province: 'Saraburi',
    region: 'Central',
    lat: 14.5289,
    lon: 100.9101,
    efficiency: 158,
    isOnline: true,
    hasAlarm: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2800,
      electricityConsumption: 2500,
      accumulatedRth: 3600
    }
  },
  {
    id: '8',
    code: 'NPATH',
    name: 'Nakhon Pathom',
    province: 'Nakhon Pathom',
    region: 'Central',
    lat: 13.8199,
    lon: 100.0449,
    efficiency: 185,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3100,
      electricityConsumption: 2800,
      accumulatedRth: 3900
    }
  },
  // Northern Region
  {
    id: '9',
    code: 'CMAI',
    name: 'Chiang Mai',
    province: 'Chiang Mai',
    region: 'Northern',
    lat: 18.7883,
    lon: 98.9853,
    efficiency: 108,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3500,
      electricityConsumption: 3200,
      accumulatedRth: 4400
    }
  },
  {
    id: '10',
    code: 'CRAI',
    name: 'Chiang Rai',
    province: 'Chiang Rai',
    region: 'Northern',
    lat: 19.9105,
    lon: 99.8406,
    efficiency: 135,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2600,
      electricityConsumption: 2300,
      accumulatedRth: 3300
    }
  },
  {
    id: '11',
    code: 'LAMP',
    name: 'Lampang',
    province: 'Lampang',
    region: 'Northern',
    lat: 18.2888,
    lon: 99.4906,
    efficiency: 172,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2400,
      electricityConsumption: 2100,
      accumulatedRth: 3000
    }
  },
  // Northeastern Region
  {
    id: '12',
    code: 'KHON',
    name: 'Khon Kaen',
    province: 'Khon Kaen',
    region: 'Northeastern',
    lat: 16.4322,
    lon: 102.8236,
    efficiency: 128,
    isOnline: true,
    hasAlarm: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3300,
      electricityConsumption: 3000,
      accumulatedRth: 4200
    }
  },
  {
    id: '13',
    code: 'UDON',
    name: 'Udon Thani',
    province: 'Udon Thani',
    region: 'Northeastern',
    lat: 17.4138,
    lon: 102.7874,
    efficiency: 155,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3000,
      electricityConsumption: 2700,
      accumulatedRth: 3800
    }
  },
  {
    id: '14',
    code: 'NRAT',
    name: 'Nakhon Ratchasima',
    province: 'Nakhon Ratchasima',
    region: 'Northeastern',
    lat: 14.9799,
    lon: 102.0977,
    efficiency: 118,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3700,
      electricityConsumption: 3400,
      accumulatedRth: 4600
    }
  },
  {
    id: '15',
    code: 'UBON',
    name: 'Ubon Ratchathani',
    province: 'Ubon Ratchathani',
    region: 'Northeastern',
    lat: 15.2287,
    lon: 104.8564,
    efficiency: 190,
    isOnline: false,
    lastUpdated: '22-05-2025',
    metrics: {
      powerConsumption: 0,
      electricityConsumption: 0,
      accumulatedRth: 0
    }
  },
  // Eastern Region
  {
    id: '16',
    code: 'CHON',
    name: 'Chonburi',
    province: 'Chonburi',
    region: 'Eastern',
    lat: 13.3611,
    lon: 100.9847,
    efficiency: 132,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3400,
      electricityConsumption: 3100,
      accumulatedRth: 4300
    }
  },
  {
    id: '17',
    code: 'RAYO',
    name: 'Rayong',
    province: 'Rayong',
    region: 'Eastern',
    lat: 12.6814,
    lon: 101.2816,
    efficiency: 150,
    isOnline: true,
    hasAlarm: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3100,
      electricityConsumption: 2800,
      accumulatedRth: 3900
    }
  },
  {
    id: '18',
    code: 'PATT',
    name: 'Pattaya',
    province: 'Chonburi',
    region: 'Eastern',
    lat: 12.9236,
    lon: 100.8825,
    efficiency: 160,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3600,
      electricityConsumption: 3300,
      accumulatedRth: 4500
    }
  },
  // Southern Region
  {
    id: '19',
    code: 'PHUK',
    name: 'Phuket',
    province: 'Phuket',
    region: 'Southern',
    lat: 7.8804,
    lon: 98.3923,
    efficiency: 125,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3800,
      electricityConsumption: 3500,
      accumulatedRth: 4700
    }
  },
  {
    id: '20',
    code: 'HATY',
    name: 'Hat Yai',
    province: 'Songkhla',
    region: 'Southern',
    lat: 7.0069,
    lon: 100.4731,
    efficiency: 137,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 3500,
      electricityConsumption: 3200,
      accumulatedRth: 4400
    }
  },
  {
    id: '21',
    code: 'SURAT',
    name: 'Surat Thani',
    province: 'Surat Thani',
    region: 'Southern',
    lat: 9.1382,
    lon: 99.3216,
    efficiency: 178,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2900,
      electricityConsumption: 2600,
      accumulatedRth: 3700
    }
  },
  {
    id: '22',
    code: 'NSIT',
    name: 'Nakhon Si Thammarat',
    province: 'Nakhon Si Thammarat',
    region: 'Southern',
    lat: 8.4316,
    lon: 99.9630,
    efficiency: 195,
    isOnline: false,
    lastUpdated: '22-05-2025',
    metrics: {
      powerConsumption: 0,
      electricityConsumption: 0,
      accumulatedRth: 0
    }
  },
  {
    id: '23',
    code: 'KRAB',
    name: 'Krabi',
    province: 'Krabi',
    region: 'Southern',
    lat: 8.0863,
    lon: 98.9063,
    efficiency: 147,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2700,
      electricityConsumption: 2400,
      accumulatedRth: 3400
    }
  },
  {
    id: '24',
    code: 'TRAN',
    name: 'Trang',
    province: 'Trang',
    region: 'Southern',
    lat: 7.5593,
    lon: 99.6114,
    efficiency: 165,
    isOnline: true,
    lastUpdated: '23-05-2025',
    metrics: {
      powerConsumption: 2500,
      electricityConsumption: 2200,
      accumulatedRth: 3200
    }
  }
];

export const provinces = [
  'All',
  'Bangkok',
  'Pathum Thani',
  'Nonthaburi',
  'Ayutthaya',
  'Saraburi',
  'Nakhon Pathom',
  'Chiang Mai',
  'Chiang Rai',
  'Lampang',
  'Khon Kaen',
  'Udon Thani',
  'Nakhon Ratchasima',
  'Ubon Ratchathani',
  'Chonburi',
  'Rayong',
  'Phuket',
  'Songkhla',
  'Surat Thani',
  'Nakhon Si Thammarat',
  'Krabi',
  'Trang'
];

export interface SiteGeo {
  id: string;
  code: string;
  name: string;
  lat: number;
  lon: number;
  realLat: number;
  realLon: number;
  value: string;
  color: string;
  image: string;
  backgroundColor: string;
  statusColor: string;
  imageCSS: string;
  offlineIcon: string;
  disconnectIconDisplay: string;
  valueColor: string;
  updatedAt: string;
  category?: string;
  hasAlarm?: boolean;
  province?: string;
  region?: string;
}